import { requestData, requestPage, type Page, type PageQuery } from '@/lib/api-client';
import type { OrderLeg, OrderLegDetail, LegStatus, ShortLineInput, Uuid } from './types';

export type LegQuery = PageQuery & {
  pharmacyId?: Uuid;
  /**
   * Several at once, sent as a repeated param.
   *
   * The queue's tabs are grouped by what the shop has to do, so "Ready" is
   * PACKED and READY_FOR_PICKUP together. Asking for both in ONE request is
   * what makes the tab pageable: a page per status, concatenated here, has no
   * page size and no `hasMore` that means anything.
   */
  status?: readonly LegStatus[];
};

/** The shop's work queue. Scoped to your own branches by the server. */
export function listLegs(query: LegQuery = {}): Promise<Page<OrderLeg>> {
  return requestPage<OrderLeg>('/pharmacy-orders', { query });
}

/** How many legs sit behind each tab, in whole numbers rather than page counts. */
export type LegFacets = {
  total: number;
  byStatus: Partial<Record<LegStatus, number>>;
};

/**
 * The tab counts.
 *
 * A separate request from the list, and the reason is that a count taken off a
 * page is not a count: the badge on "New" used to be the length of a `limit: 50`
 * response, which reads as exact and stops at 50. The server groups over the
 * whole table and scopes it exactly as it scopes the list.
 *
 * `status` is not a parameter. The numbers say what you could switch TO, so they
 * must not narrow with the tab already selected.
 */
export function getLegFacets(pharmacyId: Uuid): Promise<LegFacets> {
  return requestData<LegFacets>('/pharmacy-orders/facets', { query: { pharmacyId } });
}

export function getLeg(legId: Uuid): Promise<OrderLegDetail> {
  return requestData<OrderLegDetail>(`/pharmacy-orders/${legId}`);
}

const action = (legId: Uuid, name: string, body?: unknown) =>
  requestData<OrderLegDetail>(`/pharmacy-orders/${legId}/${name}`, { method: 'POST', body });

export const acceptLeg = (legId: Uuid) => action(legId, 'accept');
export const startPicking = (legId: Uuid) => action(legId, 'picking');
/** Consumes the stock reservation and records which batch went in the bag; short lines name theirs. */
export const packLeg = (legId: Uuid, shortLines: ShortLineInput[] = []) =>
  action(legId, 'packed', { shortLines });
export const markReady = (legId: Uuid) => action(legId, 'ready');

export const rejectLeg = (legId: Uuid, reason: string) => action(legId, 'reject', { reason });

/** Calls the order off before packing; the customer is shown and notified with the reason. */
export const cancelLeg = (legId: Uuid, reason: string) => action(legId, 'cancel', { reason });

/** Releases just this line's stock; the rest of the order carries on. */
export const markItemUnavailable = (legId: Uuid, itemId: Uuid, reason: string) =>
  requestData<OrderLegDetail>(`/pharmacy-orders/${legId}/items/${itemId}/unavailable`, {
    method: 'POST',
    body: { reason },
  });
